import { EmoNeutral, Pack, User } from "@assets"
import { Head, Link, router } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { businessOrderPath } from "@/routes"

interface OrderItem {
  id: number
  quantity: number
  product: { id: number; name?: string }
}

interface Order {
  id: number
  order_number: string
  status: string
  created_at: string
  delivery_type: string
  total: number
  user: { id: number; name: string }
  order_items: OrderItem[]
}

interface Business {
  id: number
  business_name: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  orders: Order[]
  business: Business
  businesses: Business[]
  filters: { status?: string }
  pagination: Pagination
  status_counts: Record<string, number>
  selected_business_id: number
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "short",
  }).format(date)
}

const statusLabel: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  preparing: "Preparando",
  ready: "Listo",
  delivering: "En camino",
  completed: "Completado",
  cancelled: "Cancelado",
}

const statusVariant: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  confirmed: "outline",
  preparing: "outline",
  ready: "outline",
  delivering: "outline",
  completed: "default",
  cancelled: "destructive",
}

const shortOrderRef = (orderNumber: string) => {
  const parts = orderNumber.split("-")
  const last = parts[parts.length - 1] ?? orderNumber
  const short = last.slice(-6)
  return short.toUpperCase()
}

export default function BusinessOrdersIndex({
  orders,
  business,
  businesses,
  filters,
  pagination,
  status_counts,
  selected_business_id,
}: Props) {
  const hasOrders = (orders?.length ?? 0) > 0
  const totalOrders = Object.values(status_counts || {}).reduce(
    (a, b) => a + b,
    0,
  )

  const handleStatusFilter = (value: string) => {
    const status = value === "all" ? undefined : value
    router.get(
      "/mi-negocio/pedidos",
      { status, business_id: selected_business_id },
      { preserveState: true },
    )
  }

  const handleBusinessChange = (value: string) => {
    const id = value === "all" ? undefined : value
    router.get(
      "/mi-negocio/pedidos",
      { status: filters.status, business_id: id },
      { preserveState: true },
    )
  }

  return (
    <AppLayout>
      <Head title={`Pedidos - ${business.business_name}`} />
      <div className="mx-auto max-w-4xl space-y-4 py-4 md:py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-2">
            <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
              <Pack className="fill-primary h-8 w-8" />
            </span>
            <h1 className="text-primary flex items-center text-[21px] font-bold tracking-[0.21px] md:text-3xl">
              Pedidos de {business.business_name}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {businesses.length > 1 && (
              <Select
                value={String(selected_business_id)}
                onValueChange={handleBusinessChange}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Seleccionar negocio" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((biz) => (
                    <SelectItem key={biz.id} value={String(biz.id)}>
                      {biz.business_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={filters.status ?? "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos ({totalOrders})</SelectItem>
                <SelectItem value="pending">
                  Pendientes ({status_counts?.pending ?? 0})
                </SelectItem>
                <SelectItem value="confirmed">
                  Confirmados ({status_counts?.confirmed ?? 0})
                </SelectItem>
                <SelectItem value="preparing">
                  Preparando ({status_counts?.preparing ?? 0})
                </SelectItem>
                <SelectItem value="ready">
                  Listos ({status_counts?.ready ?? 0})
                </SelectItem>
                <SelectItem value="delivering">
                  En camino ({status_counts?.delivering ?? 0})
                </SelectItem>
                <SelectItem value="completed">
                  Completados ({status_counts?.completed ?? 0})
                </SelectItem>
                <SelectItem value="cancelled">
                  Cancelados ({status_counts?.cancelled ?? 0})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!hasOrders && (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin productos" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              Aún no tienes pedidos
            </h3>
          </div>
        )}

        <div className="space-y-3">
          {orders?.map((o) => (
            <Link
              key={o.id}
              href={businessOrderPath(o.id)}
              className="border-chart-4 block w-full border-t-[1px] p-[10px]"
            >
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-muted-foreground font-mono text-sm">
                      Pedido #{shortOrderRef(o.order_number)}
                    </span>
                    <Badge variant={statusVariant[o.status] || "default"}>
                      {statusLabel[o.status] ?? o.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="truncate font-medium">{o.user.name}</span>
                  </div>
                  <div className="text-xs font-semibold">
                    {o.delivery_type === "delivery" ? "Envio" : "Retiro"}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {o.order_items
                      ?.slice(0, 2)
                      .map((it) => `${it.product.name} x${it.quantity}`)
                      .join(", ")}
                    {(o.order_items?.length ?? 0) > 2 ? "..." : ""}
                  </div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    {formatDate(o.created_at)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-lg font-semibold">
                    {formatPrice(o.total)}
                  </div>
                </div>
              </CardContent>
            </Link>
          ))}
        </div>

        {pagination.total_pages > 1 && (
          <div className="flex justify-center gap-2 pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() =>
                router.get("/mi-negocio/pedidos", {
                  page: pagination.current_page - 1,
                  status: filters.status,
                  business_id: selected_business_id,
                })
              }
            >
              Anterior
            </Button>
            <span className="text-muted-foreground flex items-center px-3 text-sm">
              Pagina {pagination.current_page} de {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() =>
                router.get("/mi-negocio/pedidos", {
                  page: pagination.current_page + 1,
                  status: filters.status,
                  business_id: selected_business_id,
                })
              }
            >
              Siguiente
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
